import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../../i18n';
import { BONUS_INTRO_MS } from '../../../../constants/bonusConstants';
import { CATCH_THE_TRAIN_CONFIG, getPointsForTrainSize } from './constants';
import { TrainWrapper } from './TrainWrapper';

export interface CatchTheTrainPlayerProps {
  themeTitle: string;
  emoji: string;
  timeLimit: number;
  onSubmit: (answer: string, points?: number) => void;
  onScoreChange?: (score: number) => void;
  disabled?: boolean;
}

type PassPhase = 'idle' | 'pass1' | 'delay1' | 'pass2' | 'delay2' | 'pass3' | 'delay3' | 'pass4' | 'delay4' | 'pass5' | 'delay5';

interface PassSpec {
  emojiCount: number;
  durationMs: number;
}

const PASS_SPECS: [PassSpec, PassSpec, PassSpec, PassSpec, PassSpec] = [
  { emojiCount: 1, durationMs: CATCH_THE_TRAIN_CONFIG.firstPassDurationMs },
  { emojiCount: 2, durationMs: CATCH_THE_TRAIN_CONFIG.secondPassDurationMs },
  { emojiCount: 3, durationMs: CATCH_THE_TRAIN_CONFIG.thirdPassDurationMs },
  { emojiCount: 4, durationMs: CATCH_THE_TRAIN_CONFIG.fourthPassDurationMs },
  { emojiCount: 5, durationMs: CATCH_THE_TRAIN_CONFIG.fifthPassDurationMs },
];

const ANIMATION_TYPES = [...CATCH_THE_TRAIN_CONFIG.animationTypes];
function pickAnimation(): (typeof ANIMATION_TYPES)[number] {
  return ANIMATION_TYPES[Math.floor(Math.random() * ANIMATION_TYPES.length)];
}

export const CatchTheTrainPlayer: React.FC<CatchTheTrainPlayerProps> = ({
  themeTitle,
  emoji,
  timeLimit,
  onSubmit,
  onScoreChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [score, setScore] = useState(0);
  const [emojisCaught, setEmojisCaught] = useState(0);
  const [phase, setPhase] = useState<PassPhase>('idle');
  const [caughtThisPass, setCaughtThisPass] = useState(false);
  const [verticalPercent, setVerticalPercent] = useState(0.4);
  const [trainPosition, setTrainPosition] = useState<'100%' | '-50%'>('100%');
  const [submitted, setSubmitted] = useState(false);
  const trainRef = useRef<HTMLDivElement>(null);
  const onSubmitRef = useRef(onSubmit);
  const scoreRef = useRef(0);
  const submitRef = useRef(false);
  const animationTypeRef = useRef(pickAnimation());

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  const currentPassSpec =
    phase === 'pass1' ? PASS_SPECS[0]
    : phase === 'pass2' ? PASS_SPECS[1]
    : phase === 'pass3' ? PASS_SPECS[2]
    : phase === 'pass4' ? PASS_SPECS[3]
    : phase === 'pass5' ? PASS_SPECS[4]
    : null;
  const isPassActive = phase === 'pass1' || phase === 'pass2' || phase === 'pass3' || phase === 'pass4' || phase === 'pass5';

  // Start first train when countdown finishes / timer starts (same as Fast Fingers: wait BONUS_INTRO_MS)
  useEffect(() => {
    if (disabled || submitted) return;
    const t = setTimeout(() => {
      setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
      animationTypeRef.current = pickAnimation();
      setTrainPosition('100%');
      setPhase('pass1');
      setCaughtThisPass(false);
    }, BONUS_INTRO_MS);
    return () => clearTimeout(t);
  }, [disabled, submitted]);

  // Trigger train move: start at 100%, then after paint set to -50% so CSS transition runs
  useEffect(() => {
    if (!isPassActive) return;
    setTrainPosition('100%');
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTrainPosition('-50%');
      });
    });
    return () => cancelAnimationFrame(id);
  }, [phase, isPassActive]);

  // Advance phase when pass duration or delay elapses (delay phases have currentPassSpec=null so we must not guard on it)
  useEffect(() => {
    if (disabled || submitted) return;

    if (isPassActive && currentPassSpec) {
      const durationMs = currentPassSpec.durationMs;
      const t = setTimeout(() => {
        setPhase((p) => {
          if (p === 'pass1') return 'delay1';
          if (p === 'pass2') return 'delay2';
          if (p === 'pass3') return 'delay3';
          if (p === 'pass4') return 'delay4';
          if (p === 'pass5') return 'delay5';
          return p;
        });
      }, durationMs);
      return () => clearTimeout(t);
    }

    if (phase === 'delay1') {
      const t = setTimeout(() => {
        setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
        setTrainPosition('100%');
        setPhase('pass2');
        setCaughtThisPass(false);
      }, CATCH_THE_TRAIN_CONFIG.delayAfterFirstPassMs);
      return () => clearTimeout(t);
    }
    if (phase === 'delay2') {
      const t = setTimeout(() => {
        setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
        setTrainPosition('100%');
        setPhase('pass3');
        setCaughtThisPass(false);
      }, CATCH_THE_TRAIN_CONFIG.delayAfterSecondPassMs);
      return () => clearTimeout(t);
    }
    if (phase === 'delay3') {
      const t = setTimeout(() => {
        setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
        setTrainPosition('100%');
        setPhase('pass4');
        setCaughtThisPass(false);
      }, CATCH_THE_TRAIN_CONFIG.delayAfterThirdPassMs);
      return () => clearTimeout(t);
    }
    if (phase === 'delay4') {
      const t = setTimeout(() => {
        setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
        setTrainPosition('100%');
        setPhase('pass5');
        setCaughtThisPass(false);
      }, CATCH_THE_TRAIN_CONFIG.delayAfterFourthPassMs);
      return () => clearTimeout(t);
    }
    if (phase === 'delay5') {
      const t = setTimeout(() => {
        setVerticalPercent(CATCH_THE_TRAIN_CONFIG.verticalPositions[Math.floor(Math.random() * CATCH_THE_TRAIN_CONFIG.verticalPositions.length)] ?? 0.5);
        animationTypeRef.current = pickAnimation();
        setTrainPosition('100%');
        setPhase('pass1');
        setCaughtThisPass(false);
      }, CATCH_THE_TRAIN_CONFIG.delayBetweenRunsMs);
      return () => clearTimeout(t);
    }

    return undefined;
  }, [disabled, submitted, phase, currentPassSpec, isPassActive]);

  const handleTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || submitted || !isPassActive || caughtThisPass || !currentPassSpec) return;

      const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY;
      if (clientX == null || clientY == null) return;

      const el = trainRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Slightly expand hit area for easier tap
      const padding = 20;
      const hit =
        clientX >= rect.left - padding &&
        clientX <= rect.right + padding &&
        clientY >= rect.top - padding &&
        clientY <= rect.bottom + padding;

      if (hit) {
        setCaughtThisPass(true);
        const points = getPointsForTrainSize(currentPassSpec.emojiCount);
        setScore((prev) => {
          const next = prev + points;
          scoreRef.current = next;
          onScoreChange?.(next);
          return next;
        });
        setEmojisCaught((prev) => prev + currentPassSpec.emojiCount);
      }
    },
    [disabled, submitted, isPassActive, caughtThisPass, currentPassSpec, onScoreChange]
  );

  // Proactively submit when local timer hits timeLimit (same as Fast Fingers; server-authoritative bonus-time-up still drives disabled)
  useEffect(() => {
    if (submitRef.current || disabled) return;
    const timer = setTimeout(() => {
      if (!submitRef.current) {
        submitRef.current = true;
        setSubmitted(true);
        onSubmitRef.current('time-up', scoreRef.current);
      }
    }, timeLimit);
    return () => clearTimeout(timer);
  }, [timeLimit, disabled]);

  // Backup: when disabled becomes true (e.g. bonus-time-up), submit if not yet done
  useEffect(() => {
    if (disabled && !submitRef.current) {
      submitRef.current = true;
      setSubmitted(true);
      onSubmitRef.current('time-up', scoreRef.current);
    }
  }, [disabled]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 140px)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
          fontWeight: 700,
          color: 'var(--text-primary)',
          padding: '0.75rem 1rem 0.4rem',
        }}
      >
        {themeTitle}
      </div>

      <div
        style={{
          flexShrink: 0,
          fontSize: '1.5rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          color: score >= 0 ? '#34D399' : '#EF4444',
        }}
      >
        {t('bonus.player.scoreLabel', { score })}
      </div>

      {/* Full-screen play area below title: train moves R→L */}
      <div
        role="button"
        tabIndex={0}
        onMouseDown={handleTap}
        onTouchStart={handleTap}
        style={{
          flex: '1 1 auto',
          minHeight: 120,
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          cursor: 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'opacity 0.3s',
        }}
      >
        {isPassActive && currentPassSpec && (
          <div
            ref={trainRef}
            style={{
              position: 'absolute',
              top: `${verticalPercent * 100}%`,
              left: trainPosition,
              transform: 'translateY(-50%)',
              transition: `left ${currentPassSpec.durationMs}ms linear`,
              willChange: 'left',
              zIndex: 1,
              width: 'max-content',
              minWidth: 'max-content',
            }}
            className="catch-the-train-train"
          >
            <TrainWrapper animationType={animationTypeRef.current}>
              <span
                style={{
                  fontSize: CATCH_THE_TRAIN_CONFIG.emojiSizePx,
                  lineHeight: 1,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                {emoji.repeat(currentPassSpec.emojiCount)}
              </span>
            </TrainWrapper>
          </div>
        )}
      </div>
    </div>
  );
}
